import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ScrollArea from 'react-scrollbar';
import ReactTable from 'react-table';
import moment from 'moment-timezone';
import i18next from 'i18next';

import { BaseTable } from '../../helpers';
import { AlertsService } from '../../services';
import { ScrollModule } from '../ScrollModule';

const EventNotesReportEvents = ({query, dataFetched}) => {
  const DATE_FORMAT = "L";

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [pages, setPages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [totalElements, setTotalElements] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const [searchValue, setSearchValue] = useState('');
  const eventsTableRef = useRef(null);

  const convertAtDate = (timestamp, timezone, start) => {
    if (start) {
      return timezone ? moment(timestamp, DATE_FORMAT).tz(timezone).startOf("day").utc().valueOf() : moment(timezone, DATE_FORMAT).startOf("day").utc().valueOf();
    } else {
      return timezone ? moment(timestamp, DATE_FORMAT).tz(timezone).endOf("day").utc().valueOf() : moment(timestamp, DATE_FORMAT).endOf("day").utc().valueOf();
    }
  };

  const fetchData = (tableState, localGrouping, startAtDate, endAtDate) => {
    setLoading(true);

    let pagedOptions = {};
    
    if (tableState?.page !== undefined) {
        pagedOptions["page"] = tableState?.page;
    }

    if (tableState?.pageSize !== undefined) {
        pagedOptions["size"] = tableState?.pageSize;
    }

    if (tableState?.sorted !== undefined && tableState?.sorted[0] !== undefined) {
        let order = tableState?.sorted[0].desc ? "desc" : "asc";
        pagedOptions["sort"] = `${tableState?.sorted[0].id},${order}`;
    }

    let options = BaseTable.buildFetchDataOptions(pagedOptions, []);
    options.push({groupingId: localGrouping?.id});
    options.push({start: convertAtDate(startAtDate, localGrouping?.timezone, true)});
    options.push({end: convertAtDate(endAtDate, localGrouping?.timezone, false)});

    AlertsService.getEventNotesReport(options).then((response) => {
      setData(response.events);
      setFilteredData(response.events);
      setPages(Math.ceil(response.page.totalElements / tableState?.pageSize));
      setPage(tableState?.page);
      setPageSize(tableState?.pageSize);
      setTotalElements(response.page.totalElements);
      setLoading(false);

      dataFetched();
    }).catch((error) => {
      console.error(error);
      setLoading(false);
    });
  };

  useEffect(() => {
    fetchData(eventsTableRef.current?.state, query.location.grouping, query.startAtDate, query.endAtDate);
  }, [query]);

  useEffect(() => {
    // TODO this really needs to call the fetch again but that will require a modification of the API
    setFilteredData(data.filter((row) => (searchValue !== '' ? row.alertConfiguration.name.toLowerCase().includes(searchValue.toLowerCase()) : true)));
  }, [searchValue])

  let options = {};
  options["page"] = page;
  options["pageSize"] = pageSize;
  let dynamicOptions = BaseTable.buildDynamicOptions(options, false, []);

  return (
      <div className="device-detail-report col-sm-12">
          <div className="op-content-box">
              <ul className="list-inline clearfix">
                  <li className="pull-left box-label">
                      <h4 className="remove-margin-bottom">{i18next.t("results")}</h4>
                  </li>
              </ul>
              <ScrollModule />
              <ScrollArea speed={0.8} className="scroll-container" contentClassName="scrollable-content" horizontal={true}>
                  <div className="monitoring-location-map">
                      <div className="react-table-custom">
                          <ReactTable
                              className="-striped -highlight"
                              ref={eventsTableRef}
                              manual // Forces table not to paginate or sort automatically, so we can handle it server-side
                              data={filteredData}
                              pages={pages} // Display the total number of pages
                              loading={loading} // Display the loading overlay when we need it
                              showPaginationTop
                              totalElements={totalElements}
                              onFetchData={() => fetchData(eventsTableRef.current?.state, query.location.grouping, query.startAtDate, query.endAtDate)}
                              columns={[
                                  {
                                      Header: i18next.t("table-headers.event-name"),
                                      accessor: "alertConfiguration.name",
                                      Cell: ({ original }) => <EventNameCell eventId={original.id} groupingId={query.location.grouping.id} alertConfiguration={original.alertConfiguration} />,
                                      sortable: false,
                                      width: 200,
                                      minResizeWidth: 50,
                                      filterable: true,
                                      Filter: (
                                          <div>
                                              <input
                                                  style={{ width: "100%", margin: "0 auto" }}
                                                  value={searchValue}
                                                  onChange={({ target }) => setSearchValue(target.value)}
                                                  type="text"
                                                  name="search"
                                                  placeholder={i18next.t("form-labels.search-by-name")}
                                                  className="table-search"
                                                  autoComplete="off"
                                              />
                                          </div>
                                      ),
                                  },
                                  {
                                      Header: i18next.t("table-headers.asset"),
                                      accessor: "originatingMeasure.entityDisplayName",
                                      sortable: false,
                                      width: 160,
                                      minResizeWidth: 50,
                                  },
                                  {
                                      Header: i18next.t("table-headers.level"),
                                      accessor: "alertLevel",
                                      Cell: ({ value }) => <LevelCell token={value.token} />,
                                      sortable: true,
                                      width: 140,
                                      minResizeWidth: 50,
                                  },
                                  {
                                      Header: i18next.t("table-headers.time"),
                                      accessor: "occurredAt",
                                      Cell: ({ original, value }) => <NormalTimeCell timezone={original.timezone} time={value} />,
                                      sortable: true,
                                      width: 140,
                                      minResizeWidth: 50,
                                  },
                                  {
                                      Header: i18next.t("table-headers.notes"),
                                      accessor: "alertNotes",
                                      Cell: ({ original }) => <NotesCell responses={original.responses} alertNotes={original.alertNotes} timezone={original.timezone} />,
                                      sortable: false,
                                      minResizeWidth: 50,
                                  },
                              ]}
                              {...dynamicOptions}
                          />
                      </div>
                  </div>
              </ScrollArea>
          </div>
      </div>
  );
}

const LevelCell = ({token}) => <span className={`criticality-indicator criticality-${token}`}>{i18next.t(`alert-levels.${token}`)}</span>;

const EventNameCell = ({eventId, groupingId, alertConfiguration}) => <span><Link to={`/monitoring/locations/${groupingId}/events/${eventId}`}>{alertConfiguration.name}</Link></span>;

const NormalTimeCell = ({timezone, time}) => {
  let fullDT = timezone ? moment(time).tz(timezone) : moment(time);
  return <span>{fullDT.format('L')}<br/>{fullDT.format('LTS zz')}</span>;
};

const NotesCell = ({responses, alertNotes, timezone}) => {
  let sortedResponses = responses.sort((a, b) => b.createdAt - a.createdAt).map(response => ({...response}));
  if (alertNotes) {
    sortedResponses.push({
      id: -1,
      createdAt: 0,
      note: alertNotes,
      responseType: {token: 'note'},
      user: {firstName: 'System', lastName: 'User', username: ''}
    });
  }
  
  return (
    <ul>
      {sortedResponses.map(response => {
        let fullDT = timezone && timezone ? moment(response.createdAt).tz(timezone) : moment(response.createdAt);
        return (<li key={response.id}>{fullDT.format('L LTS zz')}, {response.user.firstName} {response.user.lastName} <br/> {response.note}</li>);
      })}
    </ul>
  );
};

export default EventNotesReportEvents;